import ActivityBanner from "@/components/ActivityBanner";
import LayoutFooter, { getFooterDataSource } from "@/components/LayoutFooter";
import { CACHEOR_KEYS } from "@/constants/cacheor";
import { MATERIAL_CATEGORY } from "@/constants/pin";
import { isBigCompanyByUser } from "@/constants/vip";

import useAppStore from "@/context/store";
import MaterialCalendar from "@/pageComponents/MaterialCalendar";
import MaterialCollection from "@/pageComponents/MaterialCollection";
import MaterialFooter from "@/pageComponents/MaterialFooter";
import MaterialHeader from "@/pageComponents/MaterialHeader";
import MaterialHotSubject from "@/pageComponents/MaterialHotSubject";
import MaterialTopic from "@/pageComponents/MaterialTopic";
import logger from "@/server/logger";
import {
  getCalendarList,
  getQingzhouConfig,
  getSeoInlinks,
  getTopicDetail,
  getTopicPins,
} from "@/services";
import { isGdMerged } from "@/utils/gaoding";
import { isBigCompanyByGdVip } from "@/utils/gaodingVip";
import { flatFilters } from "@/utils/getFilters";
import useListenPinsPopstate from "@/utils/hooks/useListenPinsPopstate";
import { useIsMaterialVip } from "@/utils/hooks/useVip";
import { cacheor } from "@/utils/server/cacheor";
import { isLogined } from "@/utils/server/isLogined";
import { get, isArray, shuffle } from "lodash";
import Head from "next/head";
import { useEffect, useMemo, useState } from "react";
import styles from "./index.module.less";

/**模块类型 */
const ModuleType = {
  /**顶部banner */
  banner: "material--top_banner",
  /**花瓣热搜 */
  hotTopic: "material--hot_topic",
  /**热点日历 */
  calender: "material--calendars",
  /**设计趋势 */
  designTrend: "material--design_trend_topic",
  /**内容合集 */
  collection: "material--content_collection",
  /**底部会员文案 */
  footer: "material--footer_text",
} as const;

/**
 * 是否是视频或者摄影图片类型素材，用于大企业屏蔽
 * 仅用于素材首页内容判断
 */
function isVideoOrPicture(material: {
  name: string;
  firstPins?: { pins?: Pick<IPin, "file_material">[] }[];
}) {
  const { picture, video } = MATERIAL_CATEGORY;
  const pins = material.firstPins[0].pins;
  // 当有采集内容时，根据采集的素材类型进行判断
  if (pins?.length > 0) {
    const category = pins[0].file_material?.material_category;
    return category === picture || category === video;
  } else {
    // 没有预览采集内容时，用标题判断
    return ["实拍视频", "摄影图片"].includes(material.name);
  }
}

export default function MaterialPage({
  bannerConfig,
  list,
  calendarsList,
  calendarsPins,
  footer,
  layoutFooterDataSource,
}) {
  const { wrapperWidth, user, vips } = useAppStore((store) => ({
    wrapperWidth: store.wrapperWidth,
    user: store.user,
    vips: store.vips,
  }));

  /** 大企业会员用户 */
  const isBigCompany = isGdMerged(user)
    ? isBigCompanyByGdVip(vips as IGdVip[])
    : isBigCompanyByUser(user);

  const containerWidth = wrapperWidth >= 1296 ? 1296 : wrapperWidth;
  const isSmall = wrapperWidth < 1296;

  const defaultBanner = useMemo(() => {
    return bannerConfig?.materials?.find(
      (item) => item?.material?.type === "default"
    );
  }, [bannerConfig]);

  // 活动banner（倒计时）是否结束
  const [isEnd, setIsEnd] = useState(false);

  const activityBanner = useMemo(() => {
    if (!bannerConfig) {
      return null;
    }

    const banner = bannerConfig?.materials?.find(
      (item) => item?.material?.type === "activity"
    )?.material;

    if (!banner) {
      return null;
    }

    const targetDate = banner.countDown?.targetDate;

    if (targetDate) {
      // 计算倒计时有没有结束
      const isEnd = targetDate * 1000 - Date.now() <= 0;
      if (isEnd) {
        return null;
      }
    }

    return {
      foreground: [{ url: banner.front_img_url }],
      background: [{ url: banner.banner_img_url }],
      countDown: {
        ...banner.countDown,
        targetDate: targetDate ? +targetDate * 1000 : null,
        onEnd: () => {
          setIsEnd(true);
        },
      },
      href: banner.action_link,
      buttons: banner.buttons,
    };
  }, [bannerConfig]);

  const isVip = useIsMaterialVip();

  const hasActivityBanner = !!activityBanner && !isEnd && !isVip;

  useListenPinsPopstate();

  // 进入素材页面时清空 pinStack，避免返回时跳转到错误的页面
  useEffect(() => {
    const { clearPinStack, pinStack } = useAppStore.getState();
    if (pinStack && pinStack.length > 0) {
      clearPinStack();
    }
  }, []);

  return (
    <div className={styles.material}>
      <Head>
        <title>花瓣素材-设计素材图库-专业设计模版下载</title>
        <meta
          name="keywords"
          content="花瓣素材, 设计素材, 设计图库, 设计模板, 图片素材, 设计模板下载, 免费素材, 平面模板, 矢量图, 视频模板, 背景图片, 摄影图片, 免抠元素"
        />
        <meta
          name="description"
          content="花瓣网为您精选平面模板、免抠元素、基础背景、摄影图片、实拍视频、视频模板等相关素材图库，更多设计灵感、设计模板尽在花瓣网。花瓣网, 设计师寻找灵感的天堂！"
        />
      </Head>
      {/* h1 标签为了seo单独做的 */}
      <div className="golbalHiddenH1">
        <h1>花瓣素材 让灵感到创作更简单</h1>
      </div>

      {hasActivityBanner ? (
        <ActivityBanner {...activityBanner} />
      ) : (
        <div
          className={styles.bg}
          style={{
            backgroundImage: `url("${defaultBanner?.material?.banner_img_url}")`,
          }}
        ></div>
      )}

      <div
        className={styles.wrapper}
        style={{
          width: `${containerWidth}px`,
          marginTop: hasActivityBanner ? -20 : 0,
        }}
      >
        {!hasActivityBanner && <MaterialHeader />}
        {list?.map((item) => {
          if (item.type === ModuleType.hotTopic) {
            return (
              <MaterialHotSubject
                key={item.id}
                className={styles.item}
                data={item}
              />
            );
          } else if (item.type === ModuleType.collection) {
            return item.list?.map((materials, index) => {
              if (!materials.firstPins?.length) {
                console.warn(
                  `"${materials.title}"模块数据异常，没有采集内容，请检查配置`
                );
                return null;
              }

              // 大企业屏蔽 实拍视频, 摄影图片
              if (isBigCompany && isVideoOrPicture(materials)) {
                return null;
              }
              return (
                <MaterialCollection
                  key={index}
                  className={styles.item}
                  data={materials}
                  isSmall={isSmall}
                ></MaterialCollection>
              );
            });
          } else if (
            item.type === ModuleType.calender &&
            calendarsList?.length
          ) {
            return (
              <MaterialCalendar
                key={item.id}
                className={styles.item}
                data={item}
                isSmall={isSmall}
                calendarsList={calendarsList}
                serverResult={calendarsPins}
              ></MaterialCalendar>
            );
          } else if (item.type === ModuleType.designTrend) {
            return (
              <MaterialTopic
                key={item.id}
                className={styles.item}
                data={item}
              />
            );
          }
        })}
        <MaterialFooter data={footer} />

        <LayoutFooter dataSource={layoutFooterDataSource} />
      </div>
    </div>
  );
}

function randomCollectionPins(list) {
  const collection = list.find((item) => item.type === ModuleType.collection);
  collection?.list?.forEach((item) =>
    item.firstPins.forEach((topic) => (topic.pins = shuffle(topic.pins)))
  );
}

function randomCalenderPins(list) {
  list?.forEach((item) => (item.pins = shuffle(item.pins)));
}

export async function getServerSideProps({ query, req }) {
  const [cache, inlinks] = await Promise.all([
    cacheor.get({ key: CACHEOR_KEYS.MATERIAL_HOME_PAGE }),
    isLogined(req)
      ? []
      : getSeoInlinks({
          title: "花瓣素材-免费设计素材图库-专业设计模板下载",
          current_url: req.url,
        }),
  ]);

  const layoutFooterDataSource = await getFooterDataSource({
    query,
    seoLinkInfo: { currentUrl: req.url },
  });

  try {
    if (cache && !query?._cache) {
      randomCollectionPins(cache.list || []);
      randomCalenderPins(cache.calendarsPins || []);
      return {
        props: {
          ...cache,
          inlinks,
          layoutFooterDataSource,
        },
      };
    }
  } catch (error) {
    // 兼容缓存错误数据的场景
    logger.error(error);
  }

  const [configResult, calendarsListResult] = await Promise.all([
    getQingzhouConfig({
      position: "material",
      ignoreError: true,
    }),
    getCalendarList(
      {
        page_num: 1,
        page_size: 20,
        load_filters: 1,
        fields: "CALENDAR",
      },
      { ignoreError: true }
    ),
  ]);

  let calendarsList = isArray(calendarsListResult?.data)
    ? calendarsListResult.data
    : [];
  calendarsList =
    calendarsList.map((calendar) => {
      return {
        ...calendar,
        filters: flatFilters(calendar?.filters)?.map((item) => {
          return {
            id: item.id,
            name: item.origin?.title || item.name,
          };
        }),
      };
    }) || null;
  let calendarsPins = [];

  if (calendarsList?.[0]?.filters?.length) {
    let flatFiltersArr = calendarsList?.[0]?.filters;

    // 取列表第一个，第一个筛选器信息，去请求列表数据
    let result = await Promise.allSettled(
      flatFiltersArr?.map((item) => {
        return getTopicPins({
          filter_ids: [item.id],
          sort: "all",
          page: 1,
          per_page: 6,
          position: "topics",
          text: "",
        });
      })
    );

    let fulfilledResults = [];
    result.forEach((res, index) => {
      if (res.status === "fulfilled") {
        let { name, id } = flatFiltersArr[index] || {};
        let data = {
          ...res.value,
          filter: { name, id },
          pins:
            res.value.pins?.map((pin) => {
              return {
                pin_id: pin.pin_id,
                raw_text: pin.raw_text,
                file: pin.file,
              };
            }) || [],
        };
        fulfilledResults.push(data);
      }
    });
    calendarsPins = fulfilledResults;
  }
  const getFirstTabPins = (item) => {
    return item?.list?.map(async (module) => {
      // 获取第一个专题id
      const topicId = module?.scene?.[0]?.id;
      if (!topicId) {
        return undefined;
      }

      // 获取主题信息
      let topic = await getTopicDetail({
        urlname: topicId,
        preview_limit: 5,
        preview_cache: !query?._cache,
        preview_material: 1,
      });
      let fulfilledResults = topic?.content?.materials?.map((item) => {
        const { preview, material } = item;
        return {
          pins: preview?.pins?.map((item) => {
            const { file, file_material, pin_id } = item;
            const material_category = get(
              file_material,
              "material_category",
              ""
            );
            const videoSrc = get(file_material, "extra.video_mask", "");
            return {
              file,
              file_material: {
                material_category,
                extra: { video_mask: videoSrc },
              },
              pin_id,
            };
          }),
          pin_count: preview?.pin_count,
          filter: {
            value: material?.filter.value,
            title: material?.title,
          },
          ...module?.scene?.[0],
        };
      });
      return { ...module, firstPins: fulfilledResults || [] };
    });
  };

  let list = configResult?.pits
    ? configResult?.pits
        .sort((a, b) => a?.priority - b?.priority)
        .map(async (item) => {
          let data = {
            id: item?.id,
            name: item?.name,
            type: item?.mark,
            list:
              item?.materials
                ?.map((item) => ({
                  id: item?.id,
                  trackerTitle: item.title,
                  ...item?.material,
                }))
                ?.sort((a, b) => a?.priority - b?.priority) || [],
          };
          // 合集模块需要获取第一个tab数据
          if (data.type === ModuleType.collection) {
            data.list = await Promise.all(getFirstTabPins(data));
          }
          return data;
        })
    : [];
  list = await Promise.all(list);

  //过滤顶部banner，底部会员文案，和数据为空的模块
  list =
    list?.filter(
      (item) =>
        (item.type === ModuleType.calender || item.list?.length > 0) &&
        ![ModuleType.banner, ModuleType.footer].includes(item.type)
    ) || [];

  const bannerConfig = configResult?.pits?.find(
    (item) => item.mark === ModuleType.banner
  );

  const footerData = configResult?.pits
    ?.find((item) => item.mark === ModuleType.footer)
    ?.materials?.pop();

  const footer = footerData
    ? { ...footerData.material, id: footerData.id }
    : null;

  const data = {
    bannerConfig,
    list,
    calendarsList,
    calendarsPins,
    footer,
  };

  cacheor.set({
    key: CACHEOR_KEYS.MATERIAL_HOME_PAGE,
    value: data,
    ttl: 60 * 60 * 24, // 缓存一天
  });

  return {
    props: {
      ...data,
      inlinks,
      layoutFooterDataSource,
    },
  };
}
